/* eslint-disable no-restricted-imports */
import { FC, useEffect, useMemo, useRef } from 'react';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { Img } from '@/components/blocks/MaterialImage';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { ProductType } from '@/data/types/Product';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
	FormControl,
	InputBase,
	MenuItem,
	SelectChangeEvent,
	Select,
	Stack,
	styled,
	SxProps,
	Typography,
} from '@mui/material';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { BundleSelectedSize } from '@/data/Content/BundleDetailsTable';
import { Linkable } from '@/components/blocks/Linkable';

const ProductSizeInput = styled(InputBase)({
	width: 'fit-content',
	padding: '0px',
	minWidth: '123px',
	height: '32px',
	borderRadius: '2px',
	border: '1px solid #C9C9C9',
	'& .MuiInputBase-input': {
		position: 'relative',
		backgroundColor: 'transparent',
		fontSize: 'var(--font-size-default)',
		padding: '0px 10px',
	},
	'& .MuiSelect-icon': {
		color: '#2C2C2C',
	},
	'&:hover': {
		backgroundColor: 'transparent',
	},
});

const ProductItemNameSx: SxProps = {
	fontSize: '15px',
	fontWeight: 600,
	color: '#2C2C2C',
};

const ProductItemSkuSx: SxProps = {
	fontSize: '12px',
	fontWeight: 400,
	color: '#707070',
	margin: '5px 0px 10px 0px',
};

interface BundleProductItemProps {
	sku: ProductType;
	selectedSize: BundleSelectedSize[];
	onSizeSelect: (newSelectedSize: BundleSelectedSize) => void;
}

export const BundleProductItem: FC<BundleProductItemProps> = ({
	sku,
	selectedSize,
	onSizeSelect,
}) => {
	const skus = useMemo(() => sku.sKUs || [], [sku.sKUs]);
	const alreadyAutoSelected = useRef(false);

	const sizes = useMemo(
		() =>
			skus.map((item) => ({
				label: findProductAttribute(item.attributes || [], 'Talla')?.value,
				value: item.partNumber,
			})),
		[skus]
	);

	const selectedSizeSku = useMemo(
		() =>
			skus.find((item) => selectedSize.find((size) => size.partNumber === item.partNumber))
				?.partNumber || '',
		[skus, selectedSize]
	);

	useEffect(() => {
		if (sizes.length === 1 && !selectedSizeSku && !alreadyAutoSelected.current) {
			alreadyAutoSelected.current = true;
			onSizeSelect({
				mainPartNumber: sku.partNumber,
				mainProductId: sku.uniqueID,
				partNumber: sizes[0].value,
				productId: sku.uniqueID,
				quantity: 1,
			});
		}
	}, [sizes, onSizeSelect, selectedSizeSku, sku.uniqueID, sku.partNumber]);

	const onSizeSelectHandler = (event: SelectChangeEvent) => {
		onSizeSelect({
			mainPartNumber: sku.partNumber,
			mainProductId: sku.uniqueID,
			partNumber: event.target.value,
			productId: sku.uniqueID,
			quantity: 1,
		});
	};

	return (
		<Stack direction={'row'} spacing={2}>
			<RenderProductImg
				fullImageRaw={sku.thumbnail}
				render={(image) => (
					<Linkable href={sku.seo.href}>
						<Img
							src={image}
							alt={sku.name}
							sx={{ width: '84px', height: '84px', minWidth: '84px', minHeight: '84px' }}
						/>
					</Linkable>
				)}
			/>
			<Stack>
				<Typography sx={ProductItemNameSx}>{sku.name}</Typography>
				<Typography sx={ProductItemSkuSx}>{`SKU: ${sku.partNumber}`}</Typography>
				<Stack direction={'row'} spacing={2}>
					<FormControl variant="standard">
						<Select
							id="demo-customized-select-native"
							value={selectedSizeSku}
							input={<ProductSizeInput />}
							IconComponent={KeyboardArrowDownIcon}
							displayEmpty
							onChange={onSizeSelectHandler}
						>
							{sizes.length > 1 ? (
								<MenuItem value="">
									<Typography component="span">
										<Typography component="span">{'Talla'}</Typography>
									</Typography>
								</MenuItem>
							) : null}
							{sizes.map((item) => (
								<MenuItem key={item.value} value={item.value}>
									<Typography component="span">
										<Typography component="span">{'Talla '}</Typography>
										{item.label}
									</Typography>
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<DpButton href={sku.seo.href} variant="text" size="small">
						{'Más detalles'}
					</DpButton>
				</Stack>
			</Stack>
		</Stack>
	);
};
